import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { ScanLine, FileText, Loader2, MapPin, ClipboardList, CheckCircle2, Lock } from "lucide-react";
import axios from "axios";
import { API, fileUrl } from "@/lib/api";
import { SEV_META, STATUS_META, TYPE_META, prettyType } from "@/lib/defects";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import RemediationPlanner from "@/components/RemediationPlanner";
import DefectComments from "@/components/DefectComments";

export default function SharedReport() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [err, setErr] = useState(false);
  const [pin, setPin] = useState("");
  const [pinOk, setPinOk] = useState(false);

  const reload = () => axios.get(`${API}/share/${token}`).then((r) => setData(r.data)).catch(() => {});

  useEffect(() => {
    axios.get(`${API}/share/${token}`).then((r) => setData(r.data)).catch(() => setErr(true));
  }, [token]);

  const unlock = async () => {
    if (!pin.trim()) return;
    try { await axios.post(`${API}/share/${token}/verify-pin`, { pin }); setPinOk(true); toast.success("Editing unlocked"); }
    catch { toast.error("Invalid access code"); }
  };

  const focusPin = () => { window.scrollTo({ top: 0, behavior: "smooth" }); const el = document.querySelector('[data-testid="pin-input"]'); if (el) el.focus(); };

  if (err) return <div className="min-h-screen flex items-center justify-center bg-background text-foreground">Report not found.</div>;
  if (!data) return <div className="min-h-screen flex items-center justify-center bg-background"><Loader2 className="w-6 h-6 animate-spin text-muted-foreground" /></div>;

  const { project, levels, defects, action_plans = [], comments = [], has_pin } = data;
  const sevCount = (s) => defects.filter((d) => d.severity === s).length;
  const planDefectIds = new Set(action_plans.filter((p) => p.defect_id).map((p) => p.defect_id));
  const withPlan = defects.filter((d) => planDefectIds.has(d.id)).length;
  const donePlans = action_plans.filter((p) => p.status === "complete").length;
  const remPct = defects.length ? Math.round((withPlan / defects.length) * 100) : 0;

  return (
    <div className="min-h-screen bg-background text-foreground">
      <div className="h-16 border-b border-white/10 flex items-center justify-between px-6 md:px-12 bg-[#0A0A0A] sticky top-0 z-10">
        <div className="flex items-center gap-2">
          <ScanLine className="w-5 h-5 text-[#C5A24A]" />
          <span className="font-display font-bold">Zentara <span className="text-[#C5A24A]">Integrity</span></span>
          <span className="text-muted-foreground text-sm ml-2 hidden sm:inline">Inspection Report</span>
        </div>
        <Button data-testid="shared-download-pdf" onClick={() => window.open(`${API}/share/${token}/report.pdf`, "_blank")} className="gap-2 bg-[#C5A24A] hover:bg-[#A8863A] text-white transition-colors">
          <FileText className="w-4 h-4" /> Download PDF
        </Button>
      </div>

      <div className="max-w-4xl mx-auto px-6 md:px-12 py-12">
        <p className="label-caps text-[#C5A24A] mb-2">Condition Inspection</p>
        <h1 className="font-display text-4xl sm:text-5xl font-extrabold tracking-tight">{project.name}</h1>
        <p className="text-muted-foreground mt-2">{project.address || "No address on file"}</p>

        {has_pin && (
          pinOk ? (
            <div className="mt-6 flex items-center gap-2 text-sm text-[#10B981]" data-testid="pin-ok">
              <CheckCircle2 className="w-4 h-4" /> Editing unlocked — you can create and update remediation plans below.
            </div>
          ) : (
            <div className="mt-6 flex flex-wrap items-center gap-2 bg-[#121212] border border-white/10 rounded p-3" data-testid="pin-bar">
              <Lock className="w-4 h-4 text-[#C5A24A]" />
              <span className="text-sm text-muted-foreground">Have an access code? Unlock to build & update remediation action plans.</span>
              <input value={pin} onChange={(e) => setPin(e.target.value)} onKeyDown={(e) => e.key === "Enter" && unlock()} placeholder="ACCESS CODE"
                className="sm:ml-auto w-40 bg-[#0A0A0A] border border-white/10 rounded px-3 py-1.5 text-sm font-mono tracking-widest uppercase" data-testid="pin-input" />
              <Button size="sm" onClick={unlock} className="bg-[#C5A24A] hover:bg-[#A8863A] text-black" data-testid="pin-unlock-btn">Unlock</Button>
            </div>
          )
        )}

        {defects.length > 0 && (
          <div className="mt-8 border border-white/10 bg-[#121212] rounded p-5" data-testid="remediation-summary">
            <div className="flex items-center justify-between text-sm">
              <p className="label-caps text-[#C5A24A]">Remediation progress</p>
              <p className="text-muted-foreground">{withPlan}/{defects.length} defects have a plan · {donePlans}/{action_plans.length} plans complete</p>
            </div>
            <div className="mt-3 h-2 rounded-full bg-[#0A0A0A] overflow-hidden">
              <div className="h-full rounded-full transition-all" style={{ width: `${remPct}%`, background: "#C5A24A" }} />
            </div>
            <p className="text-[11px] text-muted-foreground mt-1.5">{remPct}% of defects have an action plan in place.</p>
          </div>
        )}

        <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mt-10">
          {Object.keys(SEV_META).map((s) => (
            <div key={s} className="border border-white/10 bg-[#121212] p-5">
              <p className="label-caps" style={{ color: SEV_META[s].color }}>{SEV_META[s].label}</p>
              <p className="font-display text-3xl font-extrabold mt-2">{sevCount(s)}</p>
            </div>
          ))}
        </div>

        <div className="mt-12 space-y-10">
          {levels.map((lv) => {
            const ds = defects.filter((d) => d.level_id === lv.id);
            if (!ds.length) return null;
            return (
              <div key={lv.id}>
                <h2 className="font-display text-2xl font-bold tracking-tight border-b border-white/10 pb-3 mb-5">{lv.name} <span className="text-muted-foreground text-base font-normal">· {ds.length}</span></h2>
                <div className="space-y-4">
                  {ds.map((d, i) => {
                    const TypeIcon = TYPE_META[d.defect_type]?.icon || MapPin;
                    return (
                      <div key={d.id} className="border border-white/10 bg-[#121212] p-5">
                        <div className="flex flex-col sm:flex-row gap-5">
                          {d.photos?.[0] ? (
                            <img src={fileUrl(d.photos[0])} alt={d.title} className="w-full sm:w-40 h-40 object-cover rounded border border-white/10 shrink-0" />
                          ) : (
                            <div className="w-full sm:w-40 h-40 rounded bg-[#1E1E1E] flex items-center justify-center shrink-0"><TypeIcon className="w-8 h-8 text-muted-foreground" /></div>
                          )}
                          <div className="min-w-0 flex-1">
                            <div className="flex items-start justify-between gap-3">
                              <h3 className="font-display font-bold">#{i + 1} {d.title}</h3>
                              <Badge className="border-0 shrink-0" style={{ background: (SEV_META[d.severity] || SEV_META.medium).bg, color: (SEV_META[d.severity] || SEV_META.medium).color }}>{(SEV_META[d.severity] || SEV_META.medium).label}</Badge>
                            </div>
                            <div className="grid grid-cols-2 gap-x-6 gap-y-1 mt-3 text-sm">
                              <p><span className="text-muted-foreground">Type:</span> {prettyType(d.defect_type)}</p>
                              <p><span className="text-muted-foreground">Status:</span> {(STATUS_META[d.status] || STATUS_META.open).label}</p>
                              <p><span className="text-muted-foreground">Location:</span> {d.location_note || "—"}</p>
                              <p><span className="text-muted-foreground">Measurement:</span> {d.measurement || "—"}</p>
                            </div>
                            {d.ai_analysis?.description && <p className="text-sm text-muted-foreground mt-3">{d.ai_analysis.description}</p>}
                          </div>
                        </div>
                        {has_pin && <RemediationPlanner token={token} pin={pin} pinOk={pinOk} onNeedPin={focusPin} defectId={d.id} plans={action_plans} onRefresh={reload} />}
                        {has_pin && <DefectComments token={token} pin={pin} pinOk={pinOk} onNeedPin={focusPin} defectId={d.id} comments={comments} onRefresh={reload} />}
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
          {defects.length === 0 && <p className="text-muted-foreground">No defects recorded.</p>}
        </div>

        {has_pin && (
          <div className="mt-14">
            <h2 className="font-display text-2xl font-bold tracking-tight border-b border-white/10 pb-3 mb-5 flex items-center gap-2"><ClipboardList className="w-5 h-5 text-[#C5A24A]" /> Project-wide action plans</h2>
            <div className="border border-white/10 bg-[#121212] p-5">
              <RemediationPlanner token={token} pin={pin} pinOk={pinOk} onNeedPin={focusPin} defectId={null} plans={action_plans} onRefresh={reload} />
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
